/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  createContext,
  forwardRef,
  ReactNode,
  useContext,
  useRef,
} from "react";
import { VariableSizeList } from "react-window";
import { Dictionary } from "../../../../globalTypes";
import VirtualListboxRow from "./VirtualListboxRow";

export const LIST_PADDING = 8; // px
export const SELECT_LIST_SIZE = 8; // How many options visible at once
export const MIN_ITEM_HEIGHT = 35; // px

const getOptionListSize = (itemCount: number) => {
  const numberOfOptionsToDisplay = Math.min(SELECT_LIST_SIZE, itemCount);
  return numberOfOptionsToDisplay * MIN_ITEM_HEIGHT;
};

const addPaddingToListSize = (listSize: number) => listSize + 2 * LIST_PADDING;

const getListContainerSize = (itemCount: number) =>
  addPaddingToListSize(getOptionListSize(itemCount));

const useSizeMap = () => {
  const sizeMap = useRef<Dictionary<number, number>>({});

  const getSize = (idx: number) =>
    // Fallback třeba, jinak při prvním průběhu je výška každého
    // itemu 0 a začnou se vykreslovat všechny přes sebe
    sizeMap.current[idx] ?? MIN_ITEM_HEIGHT;
  const setSize = (idx: number, size: number) => {
    sizeMap.current = { ...sizeMap.current, [idx]: size };
  };

  return { getSize, setSize };
};

const OuterElementContext = createContext<Dictionary<string, any>>({});
const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const { ...outerProps } = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
// based on https://codesandbox.io/s/0ozmx
const VirtualListbox = forwardRef<HTMLDivElement, { children: ReactNode }>(
  (props, ref) => {
    const { children, ...otherProps } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;

    const { getSize, setSize } = useSizeMap();

    const gridRef = useRef<VariableSizeList>(null);

    const handleSizeChange = (index: number) => (size: number) => {
      setSize(index, size);
      (gridRef as any).current.resetAfterIndex(index); // Setter se bohužel zavolá až po prvním getteru :(
    };

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={otherProps}>
          <VariableSizeList
            itemData={itemData}
            height={getListContainerSize(itemCount)}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            itemSize={getSize as any}
            overscanCount={5}
            itemCount={itemCount}
          >
            {({ index, style }) => (
              <VirtualListboxRow
                style={style}
                setSize={handleSizeChange(index)}
                content={itemData[index]}
                key={index}
              />
            )}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

export default VirtualListbox;
